import {
  Alert,
  Button,
  FormControl,
  LinearProgress,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import axios, { AxiosResponse } from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { AuthContext } from '../../../../contexts/user-context/user-context';

import ConfirmAction from '../../../common/confirm-action/confirm-action';
import { ErrorsFlattenner } from '../../../common/errors-flattenner/errors-flattenner';
import { isMobile, isTablet } from 'react-device-detect';
import { emptyShelf } from './utils';
import { IWarehouseShelf } from '../../../../interfaces/delivery-company/warehouseshelf';
import { OrderType } from '../../../../interfaces/delivery-company/orders';
import MultiPackageScanner, {
  validationResult,
} from '../../../common/multi-package-scanner/multi-package-scanner';
import {
  DecodingWithOrderVal,
  generateUID,
  transformOrdersToDecodingWithOrderVal,
} from '../../../../utils/encoding';
import { getMissingPackages } from '../../../../utils/missing-packages';
import { DeliveryState } from '../../../../enums/orders';
import { DataResources, ResourceActionType, user_has_data_model_access } from '../../../../utils/permissions';

interface CreateShelfFormProps {
  defaultShelf?: IWarehouseShelf;
  successCallback?: (s: IWarehouseShelf) => void;
}
function CreateShelfForm({
  defaultShelf,
  successCallback,
}: CreateShelfFormProps) {
  const { user, config } = useContext(AuthContext);

  const [shelfData, setShelfData] = useState<IWarehouseShelf>(
    defaultShelf ?? emptyShelf
  );
  const [scannedOrders, setScannedOrders] = useState<DecodingWithOrderVal[]>(
    []
  );

  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [response, setResponse] = useState<AxiosResponse>();
  const [confirmDeleteShelf, setConfirmDeleteShelf] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const getAndSetShelfOrders = async () => {
      if (defaultShelf) {
        setIsLoading(true);
        const endpoint = `${API_ENDPOINT}/api/v1/shelf/${defaultShelf.id}/?fields=orders`;
        try {
          const response = await axios.get<IWarehouseShelf>(endpoint, config);
          setScannedOrders(
            transformOrdersToDecodingWithOrderVal(response.data.orders)
          );
          setResponse(response);
        } catch (e) {
          if (axios.isAxiosError(e)) {
            setResponse(e.response);
            setMessage(
              'Failed to get attached orders: ' + e.response?.statusText
            );
          } else {
            setMessage('Failed to get attached orders');
          }
        }
        setIsLoading(false);
      }
    };

    getAndSetShelfOrders();
  }, [defaultShelf?.id]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (shelfData) {
      setIsLoading(true);
      setResponse(undefined);
      setLoadingMessage('Saving shelf...');

      const missingPackages = getMissingPackages(scannedOrders);
      if (missingPackages.length > 0) {
        setIsLoading(false);
        setMessage(
          `The following packages are missing: ${missingPackages.map((item) => `package ${item[1]} from ${item[0]}`).join(', ')}`
        );
        return;
      }

      try {
        const endpoint = `${API_ENDPOINT}/api/v1/shelf/${shelfData.id}/`;

        let response;

        if (defaultShelf == undefined) {
          // create new
          response = await axios.post(
            `${API_ENDPOINT}/api/v1/shelves/`,
            shelfData,
            config
          );
        } else {
          // edit existing
          response = await axios.put(
            endpoint,
            {
              ...shelfData,
              orders: scannedOrders.map((item) => item.order),
            },
            config
          );
        }
        setResponse(response);
        console.log(response);

        if (response.status >= 200 && response.status < 300) {
          setMessage('Shelf saved successfully');
          successCallback && successCallback(response.data);
        }
      } catch (e: any) {
        if (axios.isAxiosError(e)) {
          setResponse(e.response);
          setMessage('Failed to save shelf: ' + e.message);
        } else {
          setMessage('Failed to save shelf');
        }
        console.log(e);
      }
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const deleteShelf = async () => {
    if (shelfData) {
      setIsLoading(true);
      setResponse(undefined);
      try {
        const response = await axios.delete(
          `${API_ENDPOINT}/api/v1/shelf/${shelfData.id}/`,
          config
        );
        setResponse(response);
        if (response.status === 204) {
          setShelfData(emptyShelf);
          setMessage('Shelf deleted successfully');
        }
      } catch (e) {
        if (axios.isAxiosError(e)) {
          setMessage('Failed to delete shelf: ' + e.message);
        } else {
          setMessage('Failed to delete shelf');
        }
        console.log(e);
      }

      setIsLoading(false);
    }
  };

  return (
    <>
      <Typography
        variant="h4"
        component="h4"
        sx={{ textAlign: 'center', mt: 3, mb: 3 }}
      >
        {defaultShelf === undefined
          ? 'Create Shelf'
          : 'Shelf: ' + defaultShelf.label}
      </Typography>

      {isLoading ? (
        <>
          {loadingMessage}
          <br />
          <LinearProgress />
        </>
      ) : (
        <>
          {message != '' && (
            <Alert
              severity={
                response && response.status >= 200 && response.status < 300
                  ? 'success'
                  : 'error'
              }
            >
              <>
                {message}

                {response && response.status >= 300 && (
                  <ErrorsFlattenner rawErrors={response.data} />
                )}
              </>
            </Alert>
          )}

          {shelfData && (
            <>
              <br></br>
              <form onSubmit={handleSubmit}>
                <FormControl fullWidth>
                  <TextField
                    autoComplete="off"
                    label="Shelf Label"
                    type="text"
                    variant="outlined"
                    required
                    value={shelfData.label}
                    onChange={(e) =>
                      setShelfData({ ...shelfData, label: e.target.value })
                    }
                  />
                </FormControl>

                {defaultShelf && (
                  <>
                    <br />
                    <br />
                    <MultiPackageScanner
                      validator={function (
                        order: DecodingWithOrderVal
                      ): validationResult {
                        if (
                          order.order.delivery_state !==
                          DeliveryState.AT_WAREHOUSE
                        ) {
                          return {
                            validated: false,
                            error: 'Package is not currently at warehouse',
                          };
                        }
                        return { validated: true };
                      }}
                      onScanResultChanged={(orders) => setScannedOrders(orders)}
                      getRenderOrdersProps={(
                        orderDecoding: DecodingWithOrderVal[]
                      ) => {
                        return {
                          sourceView: "create-shelf-form",
                          items: [],
                          selectedItems: new Map(),
                          isLoading: isLoading,
                          columns: [
                            {
                              header: <b>Order ID</b>,
                              id: 'order id',
                              display_body: (order) => <b>{order.id}</b>,
                            },
                          ],
                          getItemUID: (item: DecodingWithOrderVal) =>
                            generateUID(item),
                          fullWidth: true,
                        };
                      }}
                      allowUndoScan={true}
                      defaultScannedOrders={scannedOrders}
                      label={'Orders'}
                    />
                  </>
                )}

                <br />
                <br />
                <div
                  style={{ display: 'flex', gap: 7, justifyContent: 'center' }}
                >
                  <Button
                    size={(isMobile && !isTablet) ? 'small' : 'medium'}
                    type="submit"
                    variant="contained"
                    disabled={isLoading || (defaultShelf != undefined && !user_has_data_model_access(user, DataResources.SHELF, ResourceActionType.UPDATE))}
                  >
                    Save Shelf
                  </Button>

                  {shelfData.id !== '' && (
                    <Button
                      size={(isMobile && !isTablet) ? 'small' : 'medium'}
                      onClick={() => {
                        setConfirmDeleteShelf(true);
                      }}
                      variant="outlined"
                      color="error"
                      disabled={isLoading || (defaultShelf != undefined && !user_has_data_model_access(user, DataResources.SHELF, ResourceActionType.UPDATE))}
                    >
                      Delete Shelf
                    </Button>
                  )}
                </div>
              </form>{' '}
            </>
          )}
        </>
      )}

      <ConfirmAction
        message={'Are you sure you want to delete this shelf?'}
        action={deleteShelf}
        isOpen={confirmDeleteShelf}
        handleClose={() => setConfirmDeleteShelf(false)}
      >
        Attention: This action can not be undone
        <br />
        <Alert severity="info">
          Deleting this shelf will not delete orders belonging to this shelf.{' '}
        </Alert>
      </ConfirmAction>
    </>
  );
}

export default CreateShelfForm;
